import { appAuthenticator, Logger, useSessionStorage, AmplifyError } from '@futrli/shared-services';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FEDERATED_SIGN_IN_GENERIC_ERROR_CODE, FEDERATED_SIGN_IN_STORE_KEY } from './constants';
import { FederatedSignInStorage, FederatedIdentityProvider, FederatedSignInError } from './types';

export type HandleFederatedSignIn = (customProvider: FederatedIdentityProvider) => Promise<void>;

/**
 * React hook to handle the federatedSignIn api call for the different federated entities.
 */
export const useFederatedSignIn = () => {
  const location = useLocation();
  const [, store] = useSessionStorage<FederatedSignInStorage | null>(FEDERATED_SIGN_IN_STORE_KEY, null);
  const [
    federatedSignInError,
    storeFederatedSignInError,
    clearFederatedSignInError
  ] = useSessionStorage<FederatedSignInError | null>(FEDERATED_SIGN_IN_STORE_KEY, null, true);
  const [isFederatedSignInLoading, setIsFederatedSignInLoading] = useState(false);

  useEffect(() => {
    // Clear the error whenever the location changes
    clearFederatedSignInError();
  }, [location, clearFederatedSignInError]);

  const handleFederatedSignIn = async (
    federatedIdentityProvider: FederatedIdentityProvider,
    storage?: Pick<FederatedSignInStorage, 'originLocationPathname' | 'originLocationSearch'>
  ) => {
    try {
      setIsFederatedSignInLoading(true);

      store({
        federatedIdentityProvider,
        originLocationPathname: storage?.originLocationPathname || location.pathname,
        originLocationSearch: storage?.originLocationSearch || location.search
      });

      await appAuthenticator.federatedSignIn({ customProvider: federatedIdentityProvider });
    } catch (error) {
      const err = error as AmplifyError;
      Logger.error(String(err));
      if (typeof err !== 'string') {
        storeFederatedSignInError({
          code: err.code || FEDERATED_SIGN_IN_GENERIC_ERROR_CODE,
          message: err.message
        });
      }
      setIsFederatedSignInLoading(false);
    }
  };

  return {
    isFederatedSignInLoading,
    federatedSignInError,
    handleFederatedSignIn
  };
};
